/**
 * Filterpanel for mål-velger-modusen i «Flytt herfra»-flyten (steg 6).
 * Kompakt horisontal rad øverst i 2D-canvaset (under instruksjons-banneret):
 * tomme/plukkbare-brytere + sone/bintype/binstørrelse-nedtrekk + kandidat-teller.
 * Filterverdiene eies av Layout2DCanvas (som også henter kandidatene, debouncet).
 */

import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useLayoutEditor } from '../contexts/LayoutEditorContext';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { stcBinService } from '../../../../../services/stcBinService';

/** Filterverdier for move-targets — nedtrekk holdes som strenger ('' = Alle) */
export interface MoveTargetFilters {
  emptyOnly: boolean;
  pickableOnly: boolean;
  zoneId: string;
  binTypeNo: string;
  binSizeNo: string;
}

export const DEFAULT_MOVE_FILTERS: MoveTargetFilters = {
  emptyOnly: true,
  pickableOnly: false,
  zoneId: '',
  binTypeNo: '',
  binSizeNo: '',
};

/** Delmengden av filter-options vi bruker (strukturelt kompatibel med FilterOptions) */
interface MoveFilterOptions {
  stcZoneID: string[];
  binTypeNo: number[];
  binSizeNo: number[];
}

interface MoveTargetFilterPanelProps {
  filters: MoveTargetFilters;
  onFiltersChange: (filters: MoveTargetFilters) => void;
  totalCandidates: number | null;
  truncated: boolean;
  loading: boolean;
  error: string | null;
}

const MoveTargetFilterPanel: React.FC<MoveTargetFilterPanelProps> = ({
  filters,
  onFiltersChange,
  totalCandidates,
  truncated,
  loading,
  error,
}) => {
  const { warehouseId, companyNo } = useLayoutEditor();
  const { t } = useTranslation();
  const [options, setOptions] = useState<MoveFilterOptions | null>(null);

  // Hent nedtrekk-alternativene én gang (panelet monteres kun i flytte-modus)
  useEffect(() => {
    let cancelled = false;
    stcBinService.getFilterOptions(companyNo, warehouseId)
      .then(result => {
        if (!cancelled) setOptions(result);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          console.warn('Kunne ikke hente filter-alternativer for flytt:', err);
          setOptions({ stcZoneID: [], binTypeNo: [], binSizeNo: [] });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [companyNo, warehouseId]);

  const update = (patch: Partial<MoveTargetFilters>) =>
    onFiltersChange({ ...filters, ...patch });

  const checkboxClass = 'w-3.5 h-3.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500';
  const selectClass =
    'px-1.5 py-1 text-xs border border-gray-300 rounded bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 max-w-[130px]';
  const allLabel = t('manager.modules.warehouseLayout.move.filterAll', {}, 'Alle');

  return (
    <div className="absolute top-12 left-1/2 -translate-x-1/2 z-10 flex items-center gap-3 px-3 py-1.5 bg-white/95 border border-gray-200 rounded-lg shadow-lg text-xs text-gray-700 flex-wrap max-w-[92%]">
      <label className="flex items-center gap-1.5 cursor-pointer whitespace-nowrap">
        <input
          type="checkbox"
          checked={filters.emptyOnly}
          onChange={e => update({ emptyOnly: e.target.checked })}
          className={checkboxClass}
        />
        {t('manager.modules.warehouseLayout.move.filterEmptyOnly', {}, 'Kun tomme')}
      </label>

      <label className="flex items-center gap-1.5 cursor-pointer whitespace-nowrap">
        <input
          type="checkbox"
          checked={filters.pickableOnly}
          onChange={e => update({ pickableOnly: e.target.checked })}
          className={checkboxClass}
        />
        {t('manager.modules.warehouseLayout.move.filterPickableOnly', {}, 'Kun plukkbare')}
      </label>

      <label className="flex items-center gap-1 whitespace-nowrap">
        <span className="text-gray-500">
          {t('manager.modules.warehouseLayout.move.filterZone', {}, 'Sone')}
        </span>
        <select
          value={filters.zoneId}
          onChange={e => update({ zoneId: e.target.value })}
          className={selectClass}
        >
          <option value="">{allLabel}</option>
          {(options?.stcZoneID ?? []).map(zone => (
            <option key={zone} value={zone}>{zone}</option>
          ))}
        </select>
      </label>

      <label className="flex items-center gap-1 whitespace-nowrap">
        <span className="text-gray-500">
          {t('manager.modules.warehouseLayout.move.filterBinType', {}, 'Bintype')}
        </span>
        <select
          value={filters.binTypeNo}
          onChange={e => update({ binTypeNo: e.target.value })}
          className={selectClass}
        >
          <option value="">{allLabel}</option>
          {(options?.binTypeNo ?? []).map(typeNo => (
            <option key={typeNo} value={String(typeNo)}>{typeNo}</option>
          ))}
        </select>
      </label>

      <label className="flex items-center gap-1 whitespace-nowrap">
        <span className="text-gray-500">
          {t('manager.modules.warehouseLayout.move.filterBinSize', {}, 'Binstørrelse')}
        </span>
        <select
          value={filters.binSizeNo}
          onChange={e => update({ binSizeNo: e.target.value })}
          className={selectClass}
        >
          <option value="">{allLabel}</option>
          {(options?.binSizeNo ?? []).map(sizeNo => (
            <option key={sizeNo} value={String(sizeNo)}>{sizeNo}</option>
          ))}
        </select>
      </label>

      {/* Teller + status */}
      <span className="flex items-center gap-1.5 whitespace-nowrap font-medium text-emerald-700">
        {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-600" />}
        {totalCandidates !== null &&
          t(
            'manager.modules.warehouseLayout.move.candidateCount',
            { '0': totalCandidates },
            `${totalCandidates} kandidater`
          )}
      </span>
      {truncated && (
        <span className="text-red-600 font-medium whitespace-nowrap">
          {t('manager.modules.warehouseLayout.move.truncatedHint', {}, 'Avkuttet — stram inn filtrene')}
        </span>
      )}
      {error && (
        <span className="text-red-600 whitespace-nowrap">
          {t('manager.modules.warehouseLayout.move.loadTargetsFailed', {}, 'Kunne ikke hente kandidater')}
        </span>
      )}
    </div>
  );
};

export default MoveTargetFilterPanel;
